'use client';

import './menuTabNavigation.css';

import { BaseSyntheticEvent, useContext, useEffect, useRef, useState } from 'react';

import { CartContext } from '../../CartProvider/CartProvider';
import { CategoryWiseTabNavigation } from './CategoryWiseTabNavigation';
import { FEATURE_FLAGS } from '@/lib/featureFlags';
import { Loading } from '@/components/common/Icons/Loading';
import { MenuNavigationPanelContents } from './MenuNavigationPanelContents';
import ScrollableTabs from '@/components/common/Tabs/Tabs';
import { debounce } from 'lodash';
import { useFlags } from 'launchdarkly-react-client-sdk';

export function MenuTabNavigation() {
	const { storeMenuData } = useContext(CartContext);
	const [activeTab, setActiveTab] = useState(0);
	const activeTabRef = useRef(activeTab);
	const tabRefs = useRef<(HTMLDivElement | null)[]>([]);

	const {
		[FEATURE_FLAGS.singleColumnPlpFlag]: isSingleColumnPlpFlagEnabled,
		menuLongScrollingMenu,
	} = useFlags();

	//This is to keep ref in sync
	useEffect(() => {
		activeTabRef.current = activeTab;
	}, [activeTab]);

	const tabList: string[] = storeMenuData?.storeMenu?.categories
		? storeMenuData.storeMenu.categories.map((category) => category.name)
		: [];

	useEffect(() => {
		const windowHeight = window.innerHeight;
		function handleScroll() {
			let maxArea = 0;
			let visibleIndex = -1;
			tabRefs.current.forEach((section: HTMLElement | null, index: number) => {
				if (section) {
					const rect = section.getBoundingClientRect();
					//returning if element is not visible
					if (rect.bottom <= 0 || rect.top >= windowHeight) return;
					const visibleHeight = Math.min(rect.bottom, windowHeight) - Math.max(rect.top, 0);
					if (visibleHeight > maxArea) {
						maxArea = visibleHeight;
						visibleIndex = index;
					}
				}
			});
			if (visibleIndex !== -1 && visibleIndex !== activeTabRef.current) {
				activeTabRef.current = visibleIndex;
				setActiveTab(visibleIndex);
			}
		}

		const debouncedScroll = debounce(handleScroll, 100);
		window.addEventListener('scroll', debouncedScroll);

		return () => {
			window.removeEventListener('scroll', debouncedScroll);
		};
	}, []);

	const scrollToSection = (event: BaseSyntheticEvent, index: number) => {
		const categoryName = event.target.textContent;
		if (tabRefs.current && tabRefs.current[index]) {
			window.scrollTo({
				top: tabRefs.current[index]!.offsetTop - 200,
				behavior: 'smooth',
			});
		}
		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push({
			event: 'menu_category_selection',
			eventModel: {
				menu_category: categoryName,
			},
		});
	};

	return (
		<>
			{!storeMenuData ? (
				<div className="flex justify-center">
					<Loading size="large" fill="green" />
				</div>
			) : (
				<>
					{menuLongScrollingMenu && (
						<ScrollableTabs
							tabs={tabList}
							activeTab={activeTab}
							refs={tabRefs}
							scrollToSection={scrollToSection}
						/>
					)}

					<div className="items-container-root container me-auto ms-auto items-center justify-start gap-8">
						{menuLongScrollingMenu ? (
							<>
								{storeMenuData?.storeMenu?.categories?.map((category, index: number) => (
									<MenuNavigationPanelContents
										category={category}
										reference={tabRefs}
										categoryIndex={index}
										key={index}
										isSingleColumnPlpFlagEnabled={isSingleColumnPlpFlagEnabled}
									/>
								))}
							</>
						) : (
							<CategoryWiseTabNavigation />
						)}
					</div>
				</>
			)}
		</>
	);
}
